<template>
  <div class="creative-info flex flex-col h-full">
    <!-- Header -->
    <div class="flex items-center gap-1">
      <h2 class="font-bold p-0 mb-0">{{ creative?.name }}</h2>
      <ElButton
        v-if="$is_GD"
        :disabled="!canDeleteCreative(creative?.status)"
        @click="onDeleteClick"
        type="text"
        :class="['w-[40px] h-[40px] flex justify-center items-center', { 'is-disabled opacity-50': !canDeleteCreative(creative?.status) }]"
      >
        <svg class="text-gray" width="12" height="12" fill="none">
          <use :xlink:href="`#delete-outline`"></use>
        </svg>
      </ElButton>
    </div>

    <!-- Status -->
    <CampaignStatus class="status" :status="creative?.status" />

    <!-- File size -->
    <div v-if="creative?.files.length" class="w-full mt-2 block">Size: {{ formattedTotalSize }}</div>

    <!-- Section Title -->
    <div class="my-2">
      <p v-if="showRevisionInstructions" class="font-bold text-15px mb-0">Revision Instructions</p>
      <div v-else class="flex items-center gap-2">
        <button
          :class="['font-bold text-15px mb-0 p-0', activeTab === 'activity' ? 'text-dark' : 'text-gray']"
          @click="activeTab = 'activity'"
        >
          Activity
        </button>
        <!-- Gated like the composer: a role outside the conversation (traffic
             manager, project manager) would only get a 403 behind this tab. -->
        <button
          v-if="canChat"
          :class="['font-bold text-15px mb-0 p-0', activeTab === 'chat' ? 'text-dark' : 'text-gray']"
          @click="activeTab = 'chat'"
        >
          Chat
        </button>
      </div>
    </div>

    <!-- Revision Instructions -->
    <div v-if="showRevisionInstructions" class="h-full">
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
        <el-form-item prop="instructions" required>
          <el-input
            v-model="form.instructions"
            type="textarea"
            :rows="4"
            :maxlength="400"
            show-word-limit
            placeholder="Please describe the changes required"
          />
        </el-form-item>
      </el-form>

      <ElButton :disabled="!this.form.instructions" type="primary" class="mt-2 w-full" icon="el-icon-top-left" @click="submitRevision">
        Request revision
      </ElButton>
      <ElButton class="w-full mt-1 ml-0" @click="showRevisionInstructions = false"> Cancel </ElButton>
    </div>

    <!-- Activity / Chat -->
    <template v-else>
      <Activity v-if="activeTab === 'activity'" :activity="creative?.activity || []" />
      <Chat v-else :creative-id="creative?.id" :can-participate="canChat" />
    </template>

    <!-- Action Buttons -->
    <ElButton
      v-if="$is_GD"
      :disabled="!canSendToReview(creative?.status)"
      type="primary"
      icon="el-icon-s-promotion"
      class="w-full mt-3"
      @click="$emit('send-to-review')"
    >
      Send to review
    </ElButton>
    <template v-if="!showRevisionInstructions">
      <ElButton
        type="primary"
        icon="el-icon-top-left"
        class="w-full mt-1 ml-0 bg-primary/10 text-primary"
        v-if="isInRequestRevision(creative?.status) || canRequestRevisionOrApprove(creative?.status)"
        :disabled="!canRequestRevisionOrApprove(creative?.status)"
        @click="showRevisionInstructions = true"
      >
        Request revision
      </ElButton>

      <ElButton
        v-if="isInRequestRevision(creative?.status) || canRequestRevisionOrApprove(creative?.status)"
        :disabled="!canRequestRevisionOrApprove(creative?.status)"
        type="primary"
        icon="el-icon-check"
        class="w-full mt-1 ml-0"
        @click="$emit('approve')"
      >
        Approve
      </ElButton>
    </template>
    <ElButton
      v-if="canDownload(creative?.status)"
      :loading="isDownloading"
      type="primary"
      icon="el-icon-download"
      size="small"
      class="w-full mt-1 ml-0"
      @click="downloadCreativeFile(creative)"
    >
      Download
    </ElButton>
  </div>
</template>

<script>
import CampaignStatus from '@/components/social-products/social-boost/parts/CampaignStatus.vue'
import CreativesMixin from '@/mixins/social-products/Creatives.js'
import Activity from './Activity.vue'
import Chat from './Chat.vue'

export default {
  name: 'CreativeInfo',
  mixins: [CreativesMixin],
  components: {
    CampaignStatus,
    Activity,
    Chat,
  },
  props: {
    creative: {
      type: Object,
      default: () => null,
    },
    flightStatus: {
      type: Number,
      default: -1,
    },
    openChat: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // openChat comes from the notification deep link; whether the viewer may
      // actually chat is only known once computed props exist, so created() below
      // finishes the job. Anyone else stays on Activity.
      activeTab: 'activity',
      showRevisionInstructions: false,
      form: {
        instructions: '',
      },
      rules: {
        instructions: [{ required: true, message: 'Please enter revision instructions', trigger: 'blur' }],
      },
    }
  },
  computed: {
    // Mirrors REVIEWER_ROLES in SocialBoostCreativeChatController (isBM() covers
    // the same brand/agency slugs, legacy agency-client included). A role added
    // on one side without the other gets either a composer that 403s on send or
    // a tab it can never see — keep the two in step.
    canChat() {
      return this.$is_GD || this.isBM()
    },
    totalSize() {
      if (!this.creative?.files?.length) return 0
      return this.creative.files.reduce((sum, file) => sum + (file.size || 0), 0)
    },
    formattedTotalSize() {
      const size = this.totalSize
      if (!size || isNaN(size)) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(size) / Math.log(1024))
      return (size / Math.pow(1024, i)).toFixed(1) + ' ' + units[i]
    },
  },
  created() {
    if (this.openChat && this.canChat) {
      this.activeTab = 'chat'
    }
  },
  methods: {
    onDeleteClick() {
      this.$emit('delete')
    },
    submitRevision() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return
        this.$emit('request-revision', {
          creative_id: this.creative.id,
          instructions: this.form.instructions.trim(),
        })

        this.form.instructions = ''
        this.showRevisionInstructions = false
      })
    },
  },
}
</script>

<style lang="scss">
.creative-info {
  .status {
    span {
      float: none;
      margin: 0;
    }
  }
}
</style>
